import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { AlertService } from '@context/frontend/alert';
import { SplashService } from '@context/frontend/common';
import { AlertId } from '@context/shared/types/alert';
import {
	AuthService,
	InviteDetailData,
	InviteRequiresSignInData,
	InviteService,
} from '../services';

export type InviteDetailResolveResult =
	InviteDetailData | InviteRequiresSignInData;

const MAX_AUTH_WAIT_TIME = 1000; // 1 second
const AUTH_POLL_INTERVAL = 100;

const INVITE_EXPIRED_MESSAGE = 'Invite has expired';

function getErrorMessage(err: unknown): string | undefined {
	if (err instanceof Error) return err.message;
	const e = err as {
		message?: string;
		error?: string | { message?: string | string[] };
	};
	if (typeof e.error === 'string') return e.error;
	if (e.error && typeof e.error === 'object' && 'message' in e.error) {
		const m = (e.error as { message?: string | string[] }).message;
		if (Array.isArray(m)) return m[0];
		return typeof m === 'string' ? m : undefined;
	}
	return e.message;
}

function isInviteExpiredError(err: unknown): boolean {
	return getErrorMessage(err) === INVITE_EXPIRED_MESSAGE;
}

@Injectable({ providedIn: 'root' })
export class InviteDetailResolve implements Resolve<InviteDetailResolveResult | null> {
	readonly inviteService = inject(InviteService);
	readonly authService = inject(AuthService);
	readonly router = inject(Router);
	readonly alert = inject(AlertService);
	readonly splash = inject(SplashService);

	async resolve(
		route: ActivatedRouteSnapshot,
	): Promise<InviteDetailResolveResult | null> {
		this.splash.onShow();

		const id = route.queryParamMap.get('id');
		const email = decodeURIComponent(
			route.queryParamMap.get('email') ?? '',
		);

		// correct link params are required
		if (!id || !email) {
			return this.authService
				.signOut()
				.then(() => Promise.reject())
				.finally(() => this.redirect());
		}

		// Wait for Firebase auth state to settle (e.g. after sign-in from invite dialog)
		// so we don't treat a just-signed-in user as unauthenticated.
		await this.waitForAuthState();

		// Sign out when an active session does not match this invite link (e.g. signed
		// into another account, a different invite session, or an external share session).
		if (!(await this.sessionMatchesInvite(id, email))) {
			await this.authService.signOut();
			await this.waitForAuthClear();
		}

		const inviteId = this.authService.inviteId();
		const firebaseUser = this.authService.firebaseUser();

		// Already in invite session for this invite
		if (inviteId === id) {
			return this.inviteService
				.fetchInviteData(id, email)
				.catch((err) =>
					this.redirect(
						isInviteExpiredError(err)
							? 'invite-expired'
							: 'invite-not-found',
					),
				)
				.then((data) => {
					this.splash.onHide();
					return data;
				});
		}

		// Regular auth: try to load invite details (email must match signed-in user)
		if (firebaseUser && !inviteId) {
			try {
				const data =
					await this.inviteService.fetchInviteDetailsFromApi(id);
				this.splash.onHide();
				return data;
			} catch (err: unknown) {
				const status = (err as { status?: number })?.status;
				if (status === 403) {
					// Fallback: signed-in user does not match invite – sign out and reload invite
					await this.authService.signOut();
					await this.waitForAuthClear();
					this.splash.onHide();
					await this.router.navigate(['/invite'], {
						queryParams: { id, email },
					});
					return Promise.reject();
				}
				if (status === 400 && isInviteExpiredError(err)) {
					return this.redirect('invite-expired');
				}
				return this.redirect();
			}
		}

		// Not authenticated: create invite session or require sign-in
		try {
			await this.authService.authenticateForInvite(id, email);
			await new Promise((resolve) => setTimeout(resolve, 500));
			return this.inviteService
				.fetchInviteData(id, email)
				.catch((err) => {
					console.error('Issue fetching invite data', err);
					return this.redirect(
						isInviteExpiredError(err)
							? 'invite-expired'
							: 'invite-not-found',
					);
				})
				.then((data) => {
					this.splash.onHide();
					return data;
				});
		} catch (err: unknown) {
			const body = getErrorMessage(err);
			if (body === 'invite/email-already-has-account') {
				this.splash.onHide();
				return {
					requiresSignIn: true,
					inviteId: id,
					email,
				};
			}
			if (isInviteExpiredError(err)) {
				return this.redirect('invite-expired');
			}
			return this.redirect();
		}
	}

	redirect = (alertId: AlertId = 'invite-not-found') => {
		this.splash.onHide();
		return this.router.navigate(['/']).then(() => {
			this.alert.open(alertId).subscribe();
			return Promise.reject();
		});
	};

	private async waitForAuthState(): Promise<void> {
		const deadline = Date.now() + MAX_AUTH_WAIT_TIME;
		while (Date.now() < deadline) {
			if (this.authService.firebaseUser() != null) return;
			await new Promise((r) => setTimeout(r, AUTH_POLL_INTERVAL));
		}
	}

	private async waitForAuthClear(): Promise<void> {
		const deadline = Date.now() + MAX_AUTH_WAIT_TIME;
		while (Date.now() < deadline) {
			if (this.authService.firebaseUser() == null) return;
			await new Promise((r) => setTimeout(r, AUTH_POLL_INTERVAL));
		}
	}

	/**
	 * Returns true when there is no active session, or the session belongs to the
	 * invited user for this link. Returns false when signed into another account,
	 * a different invite session, or an external share session.
	 */
	private async sessionMatchesInvite(
		inviteId: string,
		email: string,
	): Promise<boolean> {
		const firebaseUser = this.authService.firebaseUser();
		if (!firebaseUser) return true;

		const { claims } = await firebaseUser.getIdTokenResult(true);
		const role = claims['role'];

		if (role === 'invite') {
			return (
				claims['inviteId'] === inviteId && firebaseUser.email === email
			);
		}

		if (role === 'share') {
			return false;
		}

		return firebaseUser.email === email;
	}
}
